/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { SegmentSelect, useLocalization } from "@lemmonhq/uikit";

export type TableAvailabilityStatus =
  | "free"
  | "upcoming"
  | "busy"
  | "available"
  | "conflict";

export interface TableSelectorTable {
  id: number;
  name: string;
  capacity?: number;
  availability?: TableAvailabilityStatus;
  nextReservationTime?: string;
  nextReservationDuration?: number;
  reservationsCount?: number;
  reservationTimes?: Array<{ time: string; duration?: number }>;
  disabled?: boolean;
  roomName?: string;
  roomRank?: number;
}

export interface TableSelectorProps {
  tables: TableSelectorTable[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  disableUnavailable?: boolean;
  selectionDisabled?: boolean;
  emptyMessage?: string;
  searchPlaceholder?: string;
  simplified?: boolean;
  showFloorPlanDividers?: boolean;
}

type AvailabilityFilter = "all" | "free" | "upcoming" | "busy";

const normalizeAvailability = (
  availability?: TableAvailabilityStatus,
): TableAvailabilityStatus => {
  if (availability === "available") return "free";
  if (availability === "conflict") return "busy";
  return availability || "free";
};

const availabilityLabels: Record<
  TableAvailabilityStatus,
  { label: string; labelKey: string; color: string }
> = {
  free: {
    label: "Free",
    labelKey: "tables.selector.free",
    color: "badge-success",
  },
  available: {
    label: "Available",
    labelKey: "tables.selector.available",
    color: "badge-success",
  },
  upcoming: {
    label: "Upcoming",
    labelKey: "tables.selector.upcoming",
    color: "badge-warning",
  },
  busy: {
    label: "Busy",
    labelKey: "tables.selector.busy",
    color: "badge-error",
  },
  conflict: {
    label: "Conflict",
    labelKey: "tables.selector.conflict",
    color: "badge-error",
  },
};

export const TableSelector = defineComponent<TableSelectorProps>({
  name: "TableSelector",
  props: [
    "tables",
    "selectedIds",
    "onChange",
    "loading",
    "error",
    "onRetry",
    "disableUnavailable",
    "selectionDisabled",
    "emptyMessage",
    "searchPlaceholder",
    "simplified",
    "showFloorPlanDividers",
  ],
  setup(props) {
    const { t } = useLocalization();
    const searchQuery = ref("");
    const availabilityFilter = ref<AvailabilityFilter>("free");
    // Floors expanded to reveal all (incl. busy/upcoming) tables — simplified mode only
    const expandedRooms = ref<Set<string>>(new Set());

    const toggleRoom = (room: string) => {
      const next = new Set(expandedRooms.value);
      if (next.has(room)) next.delete(room);
      else next.add(room);
      expandedRooms.value = next;
    };

    // Full (search-filtered, normalized) list used by the simplified grouped view.
    // Unlike `filteredTables` this keeps busy/upcoming tables so a floor can be
    // expanded to show everything.
    const simplifiedTables = computed(() => {
      let result = (props.tables || []).map((table) => ({
        ...table,
        availability: normalizeAvailability(table.availability),
      }));
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter((table) =>
          table.name.toLowerCase().includes(query),
        );
      }
      return result;
    });

    const filteredTables = computed(() => {
      const selectedIds = props.selectedIds || [];
      let result = (props.tables || []).map((table) => ({
        ...table,
        availability: normalizeAvailability(table.availability),
      }));
      const maxDisplay = 9;

      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter((table) =>
          table.name.toLowerCase().includes(query),
        );
      }

      // In simplified mode, show free tables + always include selected/assigned tables
      if (props.simplified) {
        result = result.filter(
          (table) =>
            table.availability === "free" || selectedIds.includes(table.id),
        );
      } else if (availabilityFilter.value !== "all") {
        result = result.filter(
          (table) => table.availability === availabilityFilter.value,
        );
      }

      // Sort selected tables first
      result.sort((a, b) => {
        const aSelected = selectedIds.includes(a.id) ? 0 : 1;
        const bSelected = selectedIds.includes(b.id) ? 0 : 1;
        return aSelected - bSelected;
      });

      // Limit the list for compactness
      const limited = result.slice(0, maxDisplay);

      return limited;
    });

    const filteredTablesCount = computed(() => {
      let result = (props.tables || []).map((table) => ({
        ...table,
        availability: normalizeAvailability(table.availability),
      }));

      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter((table) =>
          table.name.toLowerCase().includes(query),
        );
      }

      if (props.simplified) {
        result = result.filter((table) => table.availability === "free");
      } else if (availabilityFilter.value !== "all") {
        result = result.filter(
          (table) => table.availability === availabilityFilter.value,
        );
      }

      return result.length;
    });

    const groupedTables = computed(() => ({
      free: filteredTables.value.filter((t) => t.availability === "free"),
      upcoming: filteredTables.value.filter(
        (t) => t.availability === "upcoming",
      ),
      busy: filteredTables.value.filter((t) => t.availability === "busy"),
    }));

    const toggleTable = (table: TableSelectorTable) => {
      if (props.selectionDisabled) return;
      const availability = normalizeAvailability(table.availability);
      if (props.disableUnavailable && availability === "busy") return;
      if (table.disabled) return;

      const selectedIds = props.selectedIds || [];
      const isSelected = selectedIds.includes(table.id);
      const next = isSelected
        ? selectedIds.filter((id) => id !== table.id)
        : [...selectedIds, table.id];

      props.onChange(next);
    };

    const renderTableCard = (table: TableSelectorTable) => {
      const availability = normalizeAvailability(table.availability);
      const selectedIds = props.selectedIds || [];
      const isSelected = selectedIds.includes(table.id);
      const unavailable = props.disableUnavailable && availability === "busy";

      if (props.simplified) {
        // Compact chip style for sidebar.
        // Busy/upcoming tables are surfaced when a floor is expanded — give them a
        // colored cue so the conflict is obvious even though they remain selectable.
        const stateClass = isSelected
          ? "bg-purple-500/15 text-purple-400 border-purple-500/30"
          : availability === "busy"
            ? "bg-error/10 text-error border-error/30 hover:border-error/50"
            : availability === "upcoming"
              ? "bg-warning/10 text-warning border-warning/30 hover:border-warning/50"
              : "bg-base-100/50 text-base-content/60 border-base-300/30 hover:border-base-content/20 hover:text-accent";

        return (
          <button
            key={table.id}
            type="button"
            class={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border cursor-pointer ${stateClass} ${
              unavailable || table.disabled
                ? "opacity-40 cursor-not-allowed"
                : ""
            }`}
            title={
              availability === "busy"
                ? t("tables.selector.busy")
                : availability === "upcoming"
                  ? t("tables.selector.upcoming")
                  : undefined
            }
            onClick={() => toggleTable(table)}
          >
            <span class="material-symbols-outlined text-sm">
              table_restaurant
            </span>
            {table.name}
            {isSelected && (
              <span class="material-symbols-outlined text-xs">check</span>
            )}
          </button>
        );
      }

      // Full card style for non-simplified
      const borderColor = isSelected
        ? "border-primary"
        : "border-base-300 hover:border-base-content/20";

      return (
        <button
          key={table.id}
          type="button"
          class={`bg-base-100 l-box-shadow rounded-md border transition-all text-left shadow-sm ${borderColor} ${
            unavailable || table.disabled
              ? "opacity-60 cursor-not-allowed"
              : "cursor-pointer hover:bg-base-200/70 hover:-translate-y-0.5"
          }`}
          onClick={() => toggleTable(table)}
        >
          <div class="p-3">
            <div class="flex items-start justify-between gap-2">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <span class="font-medium text-accent truncate">
                    {table.name}
                  </span>
                  {availability !== "free" && (
                    <div
                      class={`badge badge-sm ${availabilityLabels[availability].color}`}
                    >
                      {t(availabilityLabels[availability].labelKey)}
                    </div>
                  )}
                </div>
                {table.capacity && (
                  <div class="text-xs text-base-content/60">
                    {t("tables.selector.seats")} {table.capacity}
                  </div>
                )}
                {(availability === "upcoming" || availability === "busy") &&
                  table.reservationTimes &&
                  table.reservationTimes.length > 0 && (
                    <div class="mt-2">
                      <div class="text-[11px] text-base-content/60 mb-1">
                        {t("tables.selector.reservations_today")}
                      </div>
                      <div class="flex flex-wrap gap-1">
                        {table.reservationTimes.map((reservation, idx) => (
                          <span
                            key={idx}
                            class={`badge badge-sm ${
                              availability === "busy"
                                ? "badge-error"
                                : "badge-warning"
                            }`}
                          >
                            {new Date(reservation.time).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            {reservation.duration && (
                              <span class="ml-1">
                                ({reservation.duration}m)
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
              <div class="flex-shrink-0">
                {isSelected && (
                  <span class="material-symbols-outlined text-primary text-xl">
                    check_circle
                  </span>
                )}
              </div>
            </div>
          </div>
        </button>
      );
    };

    const renderGroup = (
      title: string,
      icon: string,
      color: string,
      tables: TableSelectorTable[],
    ) => {
      if (tables.length === 0) return null;

      return (
        <div class="mb-6">
          <div class="flex items-center gap-2 mb-3">
            <span class={`material-symbols-outlined ${color}`}>{icon}</span>
            <h3 class="text-lg font-semibold text-accent">
              {title} ({tables.length})
            </h3>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {tables.map(renderTableCard)}
          </div>
        </div>
      );
    };

    const renderContent = () => {
      if (props.loading) {
        return (
          <div class="flex justify-center items-center py-12">
            <span class="loading loading-spinner loading-lg"></span>
          </div>
        );
      }

      if (props.error) {
        return (
          <div class="text-center text-base-content/70 py-8">
            <div class="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-3">
              <span class="material-symbols-outlined text-error">error</span>
            </div>
            <p class="text-sm">{props.error}</p>
            {props.onRetry && (
              <button
                type="button"
                class="btn btn-ghost btn-sm mt-3"
                onClick={props.onRetry}
              >
                {t("tables.selector.retry")}
              </button>
            )}
          </div>
        );
      }

      // In simplified mode, render grouped by room with "Assigned" bucket first.
      // Each floor shows its free tables by default and can be expanded to reveal
      // all tables (incl. busy/upcoming) via a per-floor "Show all" button.
      if (props.simplified) {
        const selectedIds = props.selectedIds || [];

        if (simplifiedTables.value.length === 0) {
          return (
            <div class="text-center  py-8">
              {props.emptyMessage || t("tables.selector.no_tables")}
            </div>
          );
        }

        const hasRooms = simplifiedTables.value.some((t) => t.roomName);

        // No room names — render flat (free + selected only, no per-floor expand)
        if (!hasRooms) {
          const flat = simplifiedTables.value.filter(
            (t) => t.availability === "free" || selectedIds.includes(t.id),
          );
          return (
            <div class="flex flex-wrap gap-1.5">
              {flat.map(renderTableCard)}
            </div>
          );
        }

        // Split into assigned and unassigned
        const assignedTables = simplifiedTables.value.filter((t) =>
          selectedIds.includes(t.id),
        );
        const unassignedTables = simplifiedTables.value.filter(
          (t) => !selectedIds.includes(t.id),
        );

        // Group unassigned by room, sorted by roomRank (floor plan order)
        const roomGroups = new Map<string, typeof simplifiedTables.value>();
        for (const table of unassignedTables) {
          const room = table.roomName || "Other";
          if (!roomGroups.has(room)) roomGroups.set(room, []);
          roomGroups.get(room)!.push(table);
        }

        // Sort rooms by rank of first table in each group
        const sortedRooms = [...roomGroups.entries()].sort((a, b) => {
          const rankA = a[1][0]?.roomRank ?? 999;
          const rankB = b[1][0]?.roomRank ?? 999;
          return rankA - rankB;
        });

        return (
          <div class="space-y-3">
            {/* Assigned bucket */}
            {assignedTables.length > 0 && (
              <div>
                <div class="text-[11px] font-semibold text-base-content/30 uppercase tracking-wider mb-1.5">
                  Assigned
                </div>
                <div class="flex flex-wrap gap-1.5">
                  {assignedTables.map(renderTableCard)}
                </div>
              </div>
            )}

            {/* Room buckets */}
            {sortedRooms.map(([roomName, tables]) => {
              const isExpanded = expandedRooms.value.has(roomName);
              const freeTables = tables.filter(
                (t) => t.availability === "free",
              );
              const hiddenCount = tables.length - freeTables.length;
              const visibleTables = isExpanded ? tables : freeTables;

              return (
                <div
                  key={roomName}
                  class={
                    props.showFloorPlanDividers
                      ? "border-b border-base-content/20 pb-3 last:border-b-0 last:pb-0"
                      : ""
                  }
                >
                  <div class="flex items-center justify-between mb-1.5">
                    <div class="text-[11px] font-semibold text-accent/70 uppercase tracking-wider">
                      {roomName}
                    </div>
                    {hiddenCount > 0 && (
                      <button
                        type="button"
                        class="inline-flex items-center gap-0.5 text-[11px] font-medium text-base-content/50 hover:text-accent transition-colors cursor-pointer"
                        onClick={() => toggleRoom(roomName)}
                      >
                        <span class="material-symbols-outlined text-sm">
                          {isExpanded ? "remove" : "add"}
                        </span>
                        {isExpanded
                          ? t("tables.selector.show_less")
                          : t("tables.selector.show_all_count", [
                              tables.length,
                            ])}
                      </button>
                    )}
                  </div>
                  {visibleTables.length > 0 ? (
                    <div class="flex flex-wrap gap-1.5">
                      {visibleTables.map(renderTableCard)}
                    </div>
                  ) : (
                    <div class="text-[11px] text-base-content/30 italic">
                      {t("tables.selector.no_free_tables")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      if (filteredTables.value.length === 0) {
        return (
          <div class="text-center  py-8">
            {props.emptyMessage || t("tables.selector.no_tables")}
          </div>
        );
      }

      return (
        <>
          {renderGroup(
            t("tables.selector.free_tables"),
            "check_circle",
            "text-green-500",
            groupedTables.value.free,
          )}
          {renderGroup(
            t("tables.selector.upcoming_tables"),
            "schedule",
            "text-yellow-500",
            groupedTables.value.upcoming,
          )}
          {renderGroup(
            t("tables.selector.busy_tables"),
            "block",
            "text-red-500",
            groupedTables.value.busy,
          )}
        </>
      );
    };

    return () => (
      <div class="flex flex-col gap-3">
        <div class="flex flex-1">
          <div class="flex flex-1">
            <div class="relative flex-1 flex">
              <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40">
                <span class="material-symbols-outlined text-sm">search</span>
              </span>
              <input
                type="text"
                placeholder={
                  props.searchPlaceholder ||
                  t("tables.selector.search_placeholder")
                }
                class="input input-bordered input-sm flex-1 text-xs pl-9 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                value={searchQuery.value}
                onInput={(e) => {
                  searchQuery.value = (e.target as HTMLInputElement).value;
                }}
              />
            </div>
          </div>

          {!props.simplified && (
            <div class="px-2">
              <SegmentSelect
                options={[
                  { value: "all", label: t("tables.selector.all_tables") },
                  { value: "free", label: t("tables.selector.free") },
                  { value: "upcoming", label: t("tables.selector.upcoming") },
                  { value: "busy", label: t("tables.selector.busy") },
                ]}
                value={availabilityFilter.value}
                onChange={(value) => {
                  availabilityFilter.value =
                    (value as AvailabilityFilter) || "all";
                }}
                className="w-full"
              />
            </div>
          )}
        </div>

        {renderContent()}
      </div>
    );
  },
});
